import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
  SelectChangeEvent,
  Stack,
  Checkbox,
  ListItemText,
  FormControlLabel,
  Tooltip,
  // ListSubheader,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { getEstrategias, getCompras } from '../services/excelService';
import { generateExcelFile } from '../services/excelGenerator';
import type { Regiao } from '../services/excelService';
import { formatCurrencyInput, onlyNumbers } from '../utils/formatters';
import type { FormData as ExcelFormData } from '../types/FormData';
import ConfiguracoesTecnicasEspeciais from './ConfiguracoesTecnicasEspeciais';

// Função debounce para otimizar performance
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

// Interface para valores do formulário
interface FormValues {
  objetivo: string;
  plataforma: string;
  device: string;
  formato: string[];
  regiao: Regiao;
  estrategiasPorFormato: Record<string, string[]>;
  comprasPorFormato: Record<string, string[]>;
  compra: string;
  ctr: string;
  dataInicio: Date | null;
  dataFim: Date | null;
  anunciante: string;
  produto: string;
  valor: string;
  valorPorFormato: Record<string, string>;
  target: string;
  idade: string[];
  interesse: string;
  praca: string;
  pracasPorFormato: Record<string, string>;
  novoInteresse: string;
  comissao: boolean;
  bv: boolean;
  ctrPorFormato: Record<string, string>;
}

// Interface para uma configuração de Programática Open
interface ConfiguracaoProgramaticaOpen {
  id: string;
  objetivo: string;
  plataforma: string;
  device: string;
  formato: string[];
  regiao: Regiao;
  estrategiasPorFormato: Record<string, string[]>;
  comprasPorFormato: Record<string, string[]>;
  target: string;
  idade: string[];
  interesse: string;
  pracasPorFormato: Record<string, string>;
  ctrPorFormato: Record<string, string>;
  valorPorFormato: Record<string, string>;
}

// Interface para uma configuração de Programática Especial
interface ConfiguracaoProgramaticaEspecial {
  id: string;
  formData: any; // Mantendo compatibilidade com o componente existente
}

const Planejador: React.FC = () => {
  // Estados para as opções dos selects
  const [objetivos, setObjetivos] = useState<string[]>([]);
  const [plataformas, setPlataformas] = useState<string[]>([]);

  const [tiposCompraPorFormato, setTiposCompraPorFormato] = useState<Record<string, string[]>>({});
  const [estrategiasPorConfiguracao, setEstrategiasPorConfiguracao] = useState<Record<string, Record<string, string[]>>>({});

  // Estado para a mensagem de erro/sucesso
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info' as 'error' | 'warning' | 'info' | 'success',
  });

  // Estado para o carregamento
  const [loading, setLoading] = useState({
    page: true,
    submit: false,
    objetivos: false,
    plataformas: false,
    estrategias: false,
    compras: false,
  });

  // Estado para os valores do formulário
  const [formValues, setFormValues] = useState<FormValues>({
    objetivo: '',
    plataforma: '',
    device: '',
    formato: [],
    regiao: 'Estados e Brasil',
    estrategiasPorFormato: {},
    comprasPorFormato: {},
    compra: '',
    ctr: '',
    dataInicio: null,
    dataFim: null,
    anunciante: '',
    produto: '',
    valor: '',
    valorPorFormato: {},
    target: '',
    idade: [],
    interesse: '',
    praca: '',
    pracasPorFormato: {},
    novoInteresse: '',
    comissao: false,
    bv: false,
    ctrPorFormato: {},
  });

  // Estados para múltiplas configurações
  const [configuracoesProgramaticaOpen, setConfiguracoesProgramaticaOpen] = useState<ConfiguracaoProgramaticaOpen[]>([]);
  const [configuracoesProgramaticaEspecial, setConfiguracoesProgramaticaEspecial] = useState<ConfiguracaoProgramaticaEspecial[]>([]);

  // Estados para habilitação automática baseada na presença de configurações
  const habilitarTecnicas = configuracoesProgramaticaOpen.length > 0;
  const habilitarEspeciais = configuracoesProgramaticaEspecial.length > 0;

  // Adicionar estado para o novo campo
  const [valorMinimoMensalEspeciais, setValorMinimoMensalEspeciais] = useState('0,00');





  // Memoizar valores computados para evitar recálculos desnecessários
  const objetivosUnicos = useMemo(() => {
    return configuracoesProgramaticaOpen
      .map(config => config.objetivo)
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index);
  }, [configuracoesProgramaticaOpen]);



  const estrategiasUnicas = useMemo(() => {
    const estrategias = new Set<string>();
    configuracoesProgramaticaOpen.forEach(config => {
      Object.values(config.estrategiasPorFormato).flat().forEach(estrategia => estrategias.add(estrategia));
    });
    return Array.from(estrategias);
  }, [configuracoesProgramaticaOpen]);

  // Função debounced para carregar plataformas
  const debouncedFetchPlataformas = useCallback(
    debounce(async (objetivo: string) => {
      if (!objetivo) {
        if (plataformas.length > 0) {
        setPlataformas([]);
        }
        return;
      }

      try {
        setLoading((prev) => ({ ...prev, plataformas: true }));
        
        const demoPlataformas: Record<string, string[]> = {
          'Awareness': ['DV360', 'GAMA'],
          'Brandformance': ['DV360', 'GAMA']
        };
        
        const novasPlataformas = demoPlataformas[objetivo] || [];
        
        if (JSON.stringify(plataformas) !== JSON.stringify(novasPlataformas)) {
          setPlataformas(novasPlataformas);
        }
      } catch (error) {
        console.error('Erro ao carregar plataformas:', error);
        setPlataformas([]);
      } finally {
        setLoading((prev) => ({ ...prev, plataformas: false }));
      }
    }, 300),
    []
  );



  // Função debounced para carregar compras
  const debouncedFetchCompras = useCallback(
    debounce(async (estrategias: string[], configs: ConfiguracaoProgramaticaOpen[]) => {
      if (estrategias.length === 0) {
        if (Object.keys(tiposCompraPorFormato).length > 0) {
          setTiposCompraPorFormato({});
        }
        return;
      }

      const estrategiasAtuais = Object.keys(tiposCompraPorFormato).sort().join(',');
      const estrategiasNovas = estrategias.sort().join(',');
      
      if (estrategiasAtuais === estrategiasNovas && Object.keys(tiposCompraPorFormato).length > 0) {
        return;
      }

      try {
        setLoading((prev) => ({ ...prev, compras: true }));
        const novasCompras: Record<string, string[]> = {};

        for (const estrategia of estrategias) {
          const configComEstrategia = configs.find(config => 
            Object.values(config.estrategiasPorFormato).flat().includes(estrategia)
          );
          if (configComEstrategia) {
            const formato = configComEstrategia.formato.find(f => 
              configComEstrategia.estrategiasPorFormato[f]?.includes(estrategia)
            );
            if (formato) {
            const compras = await getCompras(
                configComEstrategia.objetivo,
                configComEstrategia.plataforma,
              [formato],
                estrategia
            );
            novasCompras[formato] = compras;
            }
          }
        }

        const comprasAtuais = JSON.stringify(tiposCompraPorFormato);
        const comprasNovas = JSON.stringify(novasCompras);
        if (comprasAtuais !== comprasNovas) {
        setTiposCompraPorFormato(novasCompras);
        }
      } catch (error) {
        console.error('Erro ao carregar tipos de compra:', error);
        setTiposCompraPorFormato({});
      } finally {
        setLoading((prev) => ({ ...prev, compras: false }));
      }
    }, 300),
    []
  );

  // Funções para gerenciar configurações
  const adicionarConfiguracaoProgramaticaOpen = useCallback(() => {
    const novaConfiguracao: ConfiguracaoProgramaticaOpen = {
      id: Date.now().toString(),
      objetivo: '',
      plataforma: '',
      device: '',
      formato: [],
      regiao: 'Estados e Brasil',
      estrategiasPorFormato: {},
      comprasPorFormato: {},
      target: '',
      idade: [],
      interesse: '',
      pracasPorFormato: {},
      ctrPorFormato: {},
      valorPorFormato: {},
    };
    setConfiguracoesProgramaticaOpen(prev => [...prev, novaConfiguracao]);
  }, []);

  const removerConfiguracaoProgramaticaOpen = useCallback((id: string) => {
    setConfiguracoesProgramaticaOpen(prev => prev.filter(config => config.id !== id));
  }, []);

  const atualizarConfiguracaoProgramaticaOpen = useCallback((id: string, dados: Partial<ConfiguracaoProgramaticaOpen>) => {
    setConfiguracoesProgramaticaOpen(prev =>
      prev.map(config => {
        if (config.id !== id) return config;

        // Se o objetivo mudou, limpar campos dependentes
        if (dados.objetivo && dados.objetivo !== config.objetivo) {
          return {
            ...config,
            ...dados,
        plataforma: '',
        device: '',
        formato: [],
        estrategiasPorFormato: {},
            comprasPorFormato: {},
        target: '',
        idade: [],
            interesse: '',
            pracasPorFormato: {},
            ctrPorFormato: {},
            valorPorFormato: {}
          };
        }
        
        // Se a plataforma mudou, limpar campos dependentes
        if (dados.plataforma && dados.plataforma !== config.plataforma) {
          return {
            ...config,
            ...dados,
        device: '',
        formato: [],
        estrategiasPorFormato: {},
            comprasPorFormato: {},
        target: '',
        idade: [],
            interesse: '',
            pracasPorFormato: {},
            ctrPorFormato: {},
            valorPorFormato: {}
          };
        }
        
        // Se o formato mudou, limpar campos dependentes
        if (dados.formato && JSON.stringify(dados.formato) !== JSON.stringify(config.formato)) {
          return {
            ...config,
            ...dados,
        estrategiasPorFormato: {},
            comprasPorFormato: {},
        target: '',
        idade: [],
            interesse: '',
            pracasPorFormato: {},
            ctrPorFormato: {},
            valorPorFormato: {}
          };
        }
        
        // Se a região mudou, limpar campos dependentes
        if (dados.regiao && dados.regiao !== config.regiao) {
          return {
            ...config,
            ...dados,
        estrategiasPorFormato: {},
            comprasPorFormato: {},
        target: '',
        idade: [],
            interesse: '',
            pracasPorFormato: {},
            ctrPorFormato: {},
            valorPorFormato: {}
          };
        }
        
        // Se o target mudou, limpar campos dependentes
        if (dados.target && dados.target !== config.target) {
          return {
            ...config,
            ...dados,
            idade: []
          };
        }
        
        // Para outros campos, apenas atualizar
        return { ...config, ...dados };
      })
    );
  }, []);

  const adicionarConfiguracaoProgramaticaEspecial = useCallback(() => {
    const novaConfiguracao: ConfiguracaoProgramaticaEspecial = {
      id: Date.now().toString(),
      formData: null,
    };
    setConfiguracoesProgramaticaEspecial(prev => [...prev, novaConfiguracao]);
  }, []);

  const removerConfiguracaoProgramaticaEspecial = useCallback((id: string) => {
    setConfiguracoesProgramaticaEspecial(prev => prev.filter(config => config.id !== id));
  }, []);

  const atualizarConfiguracaoProgramaticaEspecial = useCallback((id: string, formData: any) => {
    setConfiguracoesProgramaticaEspecial(prev => 
      prev.map(config => 
        config.id === id ? { ...config, formData } : config
      )
    );
  }, []);

  // Função para atualizar valor mínimo de linha específica
  const atualizarValorMinimoLinha = useCallback((id: string, valor: string) => {
    setConfiguracoesProgramaticaEspecial(prev => 
      prev.map(config => 
        config.id === id ? { 
          ...config, 
          formData: config.formData ? { ...config.formData, investimentoMinimo: valor } : null 
        } : config
      )
    );
  }, []);

  // Funções memoizadas para cada configuração especial
  const getConfiguracaoEspecialHandlers = useCallback((configId: string) => ({
    onChange: (formData: any) => atualizarConfiguracaoProgramaticaEspecial(configId, formData),
    onValorMinimoLinhaChange: (valor: string) => atualizarValorMinimoLinha(configId, valor)
  }), [atualizarConfiguracaoProgramaticaEspecial, atualizarValorMinimoLinha]);

  // Resetar campos da Programática Open quando desabilitada
  useEffect(() => {
    if (!habilitarTecnicas) {
      setFormValues(prev => ({
        ...prev,
        objetivo: '',
        plataforma: '',
        device: '',
        formato: [],
        estrategiasPorFormato: {},
        comprasPorFormato: {},
        compra: '',
        ctr: '',
        target: '',
        idade: [],
      }));
    }
  }, [habilitarTecnicas]);

  // Limpar Valor Minimo Mensal (Especiais) ao desmarcar Programática Especial
  useEffect(() => {
    if (!habilitarEspeciais) {
      setValorMinimoMensalEspeciais('0,00');
    }
  }, [habilitarEspeciais]);

  // Carregar objetivos ao iniciar
  useEffect(() => {
    const fetchObjetivos = async () => {
      try {
        setLoading((prev) => ({ ...prev, objetivos: true }));
        setObjetivos(['Awareness', 'Brandformance']);
      } catch (error) {
        console.error('Erro ao carregar objetivos:', error);
        setSnackbar({
          open: true,
          message: 'Erro ao carregar dados.',
          severity: 'error',
        });
      } finally {
        setLoading((prev) => ({ ...prev, objetivos: false, page: false }));
      }
    };

    fetchObjetivos();
  }, []);

  // Carregar plataformas quando o objetivo mudar (otimizado)
  useEffect(() => {
    const objetivo = objetivosUnicos[0];
    if (objetivo) {
      debouncedFetchPlataformas(objetivo);
    }
  }, [objetivosUnicos, debouncedFetchPlataformas]);



  // Carregar estratégias para cada configuração quando necessário
  useEffect(() => {
    const carregarEstrategiasParaConfiguracao = async (config: ConfiguracaoProgramaticaOpen) => {
      if (!config.objetivo || !config.plataforma || config.formato.length === 0) {
        return;
      }

      const novasEstrategias: Record<string, string[]> = {};
      
      for (const formato of config.formato) {
        // Verificar se já temos estratégias para este formato nesta configuração
        if (!estrategiasPorConfiguracao[config.id]?.[formato]) {
          try {
            const estrategias = await getEstrategias(config.objetivo, config.plataforma, [formato]);
            if (estrategias && estrategias.length > 0) {
              novasEstrategias[formato] = estrategias;
            }
          } catch (error) {
            console.error('Erro ao carregar estratégias:', error);
          }
        }
      }

      if (Object.keys(novasEstrategias).length > 0) {
        setEstrategiasPorConfiguracao(prev => ({
        ...prev,
          [config.id]: {
            ...prev[config.id],
            ...novasEstrategias
          }
      }));
    }
  };

    // Carregar estratégias para todas as configurações
    configuracoesProgramaticaOpen.forEach(carregarEstrategiasParaConfiguracao);
  }, [configuracoesProgramaticaOpen, estrategiasPorConfiguracao, getEstrategias]);

  // Carregar compras quando a estratégia mudar (otimizado)
  useEffect(() => {
    if (estrategiasUnicas.length > 0) {
      debouncedFetchCompras(estrategiasUnicas, configuracoesProgramaticaOpen);
    }
  }, [estrategiasUnicas, configuracoesProgramaticaOpen, debouncedFetchCompras]);

  // Função para obter formatos baseado na plataforma
  const getFormatosPorPlataforma = useCallback((plataforma: string): string[] => {
    const demoFormatos: Record<string, string[]> = {
      'DV360': ['Display IAB', 'Native', 'Vídeo Open', 'Youtube'],
      'GAMA': ['Display IAB', 'Vídeo Open']
    };
    return demoFormatos[plataforma] || [];
  }, []);



  // Função para lidar com a mudança nos campos de texto
  const handleInputChange = useCallback((field: keyof FormValues) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormValues((prev) => {
      const newValues = { ...prev, [field]: value };
      
      // Reset dependent fields when a parent field changes
      if (field === 'objetivo') {
        newValues.plataforma = '';
        newValues.device = '';
        newValues.formato = [];
        newValues.estrategiasPorFormato = {};
        newValues.compra = '';
        newValues.target = '';
        newValues.idade = [];
      } else if (field === 'plataforma') {
        newValues.device = '';
        newValues.formato = [];
        newValues.estrategiasPorFormato = {};
        newValues.compra = '';
        newValues.target = '';
        newValues.idade = [];
      } else if (field === 'device') {
        newValues.formato = [];
        newValues.estrategiasPorFormato = {};
        newValues.compra = '';
        newValues.target = '';
        newValues.idade = [];
      } else if (field === 'formato') {
        newValues.estrategiasPorFormato = {};
        newValues.compra = '';
        newValues.target = '';
        newValues.idade = [];
      } else if (field === 'target') {
        newValues.idade = [];
      }
      
      return newValues;
    });
  }, []);

  // Função para formatar o valor como moeda
  const handleValorChange = useCallback((formato?: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const onlyNums = onlyNumbers(event.target.value);
    if (formato) {
      setFormValues(prev => ({
        ...prev,
        valorPorFormato: {
          ...prev.valorPorFormato,
          [formato]: onlyNums
        }
      }));
    } else {
      setFormValues(prev => ({ ...prev, valor: onlyNums }));
    }
  }, []);

  // Função para lidar com a mudança nas datas
  const handleDateChange = useCallback((field: 'dataInicio' | 'dataFim') => (date: Date | null) => {
    setFormValues((prev) => ({ ...prev, [field]: date }));
  }, []);

  // Função para fechar o snackbar
  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Função para validar campos da Programática Especial
  const validarProgramaticaEspecial = useCallback((formData: any) => {
    if (!formData) {
      return true; // Se não houver dados especiais, não precisa validar
    }

    // Validar Valor Mínimo Mensal da configuração específica
    if (!formData.investimentoMinimo || formData.investimentoMinimo === '0,00' || formData.investimentoMinimo === '') {
      setSnackbar({
        open: true,
        message: 'Por favor, preencha o Investimento Mínimo na Configuração Especial.',
        severity: 'error',
      });
      return false;
    }

    // Campos obrigatórios para todos os formatos
    const camposObrigatorios = [
      'objetivo',
      'formato',
      'plataforma',
      'estrategia',
      'target',
      'criativo',
      'tipoCompra'
    ];

    // Verificar campos obrigatórios básicos
    for (const campo of camposObrigatorios) {
      if (!formData[campo]) {
        setSnackbar({
          open: true,
          message: `Por favor, preencha o campo ${campo} na Programática Especial.`,
          severity: 'error',
        });
        return false;
      }
    }

    // Verificar idade (deve ser um array não vazio)
    if (formData.target) {
    if (!Array.isArray(formData.idade) || formData.idade.length === 0) {
      setSnackbar({
        open: true,
        message: 'Por favor, selecione pelo menos uma faixa etária na Programática Especial.',
        severity: 'error',
      });
      return false;
      }
    }

    // Verificar interesse e praca apenas se idade estiver preenchida
    if (formData.idade && formData.idade.length > 0) {
      if (!formData.interesse) {
        setSnackbar({
          open: true,
          message: 'Por favor, preencha o campo interesse na Programática Especial.',
          severity: 'error',
        });
        return false;
      }
      
      if (!formData.praca) {
        setSnackbar({
          open: true,
          message: 'Por favor, preencha o campo praça na Programática Especial.',
          severity: 'error',
        });
        return false;
      }
    }

    // Verificar CTR/VTR baseado no formato
    const formatosSemCTR = ['OTT', 'CTV', 'WhatsApp', 'Display', 'Alto Impacto'];
    const spotifyAudioSemCTR = formData.formato === 'Áudio' && 
                              formData.plataforma === 'Spotify' && 
                              ['Display', 'Audio'].includes(formData.criativo);

    if (!formatosSemCTR.includes(formData.formato) && !spotifyAudioSemCTR) {
      if (!formData.ctr) {
        const tipoMetrica = (formData.formato === 'Áudio' && 
                           formData.plataforma === 'Spotify' && 
                           formData.criativo === 'Vídeo') ? 'VTR' : 'CTR';
        
        setSnackbar({
          open: true,
          message: `Por favor, preencha o campo ${tipoMetrica} na Programática Especial.`,
          severity: 'error',
        });
        return false;
      }
    }

    return true;
  }, []);

  // Função para enviar o formulário
  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    
    // Validar campos obrigatórios básicos
    if (!formValues.anunciante || !formValues.produto || !formValues.dataInicio || !formValues.dataFim) {
      setSnackbar({
        open: true,
        message: 'Por favor, preencha todos os campos obrigatórios das Informações da Campanha.',
        severity: 'error',
      });
      return;
    }
    
    if (formValues.dataInicio && formValues.dataFim && formValues.dataInicio > formValues.dataFim) {
      setSnackbar({
        open: true,
        message: 'A data de início deve ser anterior à data de fim.',
        severity: 'error',
      });
      return;
    }

    // Validar se há pelo menos uma configuração de Programática Open ou Especial
    if (configuracoesProgramaticaOpen.length === 0 && configuracoesProgramaticaEspecial.length === 0) {
      setSnackbar({
        open: true,
        message: 'Por favor, adicione pelo menos uma configuração de Programática Open ou Programática Especial.',
        severity: 'error',
      });
      return;
    }

    // Validar todas as configurações de Programática Open
    for (let i = 0; i < configuracoesProgramaticaOpen.length; i++) {
      const config = configuracoesProgramaticaOpen[i];
      
      // Validar campos obrigatórios
      if (!config.objetivo || !config.plataforma || !config.device || config.formato.length === 0) {
        setSnackbar({
          open: true,
          message: `Por favor, preencha todos os campos obrigatórios na Configuração ${i + 1} da Programática Open.`,
          severity: 'error',
        });
        return;
      }

      // Validar se todos os formatos têm estratégias selecionadas
      const todosFormatosPossuemEstrategias = config.formato.every(
        formato => config.estrategiasPorFormato[formato]?.length > 0
      );

      if (!todosFormatosPossuemEstrategias) {
        setSnackbar({
          open: true,
          message: `Por favor, selecione estratégias para todos os formatos na Configuração ${i + 1} da Programática Open.`,
          severity: 'error',
        });
        return;
      }

      // Validar se todos os formatos têm tipo de compra selecionado
      const todosFormatosPossuemCompra = config.formato.every(
        formato => config.comprasPorFormato[formato]?.length > 0
      );

      if (!todosFormatosPossuemCompra) {
        setSnackbar({
          open: true,
          message: `Por favor, selecione o tipo de compra para todos os formatos na Configuração ${i + 1} da Programática Open.`,
          severity: 'error',
        });
        return;
      }

      // Validar se todos os formatos têm CTR/VTR definido
      const todosFormatosPossuemCTR = config.formato.every(
        formato => {
          // Não validar CTR para Display e Alto Impacto
          if (['Display', 'Alto Impacto'].includes(formato)) {
            return true;
          }
          return config.ctrPorFormato[formato];
        }
      );

      if (!todosFormatosPossuemCTR) {
        setSnackbar({
          open: true,
          message: `Por favor, defina o CTR/VTR para todos os formatos na Configuração ${i + 1} da Programática Open.`,
          severity: 'error',
        });
        return;
      }

      // Validar se todos os formatos têm valor definido
      const todosFormatosPossuemValor = config.formato.every(
        formato => config.valorPorFormato[formato] && config.valorPorFormato[formato] !== '0'
      );

      if (!todosFormatosPossuemValor) {
        setSnackbar({
          open: true,
          message: `Por favor, defina o valor para todos os formatos na Configuração ${i + 1} da Programática Open.`,
          severity: 'error',
        });
        return;
      }
    }

    // Validar todas as configurações de Programática Especial
    for (let i = 0; i < configuracoesProgramaticaEspecial.length; i++) {
      const config = configuracoesProgramaticaEspecial[i];
      
      if (!validarProgramaticaEspecial(config.formData)) {
        setSnackbar({
          open: true,
          message: `Por favor, corrija os erros na Configuração Especial ${i + 1}.`,
          severity: 'error',
        });
        return;
      }
    }

    try {
      setLoading(prev => ({ ...prev, submit: true }));

      // Criar um objeto com os dados formatados para enviar
      const dadosFormatados = {
        // Informações da Campanha
        anunciante: formValues.anunciante,
        produto: formValues.produto,
        dataInicio: formValues.dataInicio,
        dataFim: formValues.dataFim,
        comissao: formValues.comissao,
        bv: formValues.bv,
        valorMinimoMensalEspeciais: valorMinimoMensalEspeciais,
        
        // Configurações de Programática Open
        configuracoesProgramaticaOpen: configuracoesProgramaticaOpen,
        
        // Configurações de Programática Especial
        configuracoesProgramaticaEspecial: configuracoesProgramaticaEspecial,
        
        // Manter compatibilidade com o sistema existente
        objetivo: configuracoesProgramaticaOpen[0]?.objetivo || '',
        plataforma: configuracoesProgramaticaOpen[0]?.plataforma || '',
        device: configuracoesProgramaticaOpen[0]?.device || '',
        formato: configuracoesProgramaticaOpen[0]?.formato || [],
        regiao: configuracoesProgramaticaOpen[0]?.regiao || 'Estados e Brasil',
        estrategiasPorFormato: configuracoesProgramaticaOpen[0]?.estrategiasPorFormato || {},
        comprasPorFormato: configuracoesProgramaticaOpen[0]?.comprasPorFormato || {},
        target: configuracoesProgramaticaOpen[0]?.target || '',
        idade: configuracoesProgramaticaOpen[0]?.idade || [],
        interesse: configuracoesProgramaticaOpen[0]?.interesse || '',
        pracasPorFormato: configuracoesProgramaticaOpen[0]?.pracasPorFormato || {},
        ctrPorFormato: configuracoesProgramaticaOpen[0]?.ctrPorFormato || {},
        valorPorFormato: configuracoesProgramaticaOpen[0]?.valorPorFormato || {},
        habilitarEspeciais: configuracoesProgramaticaEspecial.length > 0,
        formDataEspeciais: configuracoesProgramaticaEspecial[0]?.formData || null,
        
        // Propriedades adicionais para compatibilidade
        habilitarTecnicas: configuracoesProgramaticaOpen.length > 0,
        estrategia: Object.values(configuracoesProgramaticaOpen[0]?.estrategiasPorFormato || {}).flat(),
        compra: Object.values(configuracoesProgramaticaOpen[0]?.comprasPorFormato || {})
          .filter(compras => Array.isArray(compras) && compras.length > 0)
          .map(compras => compras.join(', '))
          .join(', '),
        valor: '',
        praca: '',
        novoInteresse: '',
        ctr: Object.entries(configuracoesProgramaticaOpen[0]?.ctrPorFormato || {}).map(([formato, valor]) => 
          `${formato}: ${valor}%`
        ).join(', '),
      };

      console.log('Dados formatados para envio:', dadosFormatados);

      // Gerar a planilha
      const blob = await generateExcelFile(dadosFormatados as unknown as ExcelFormData);
      
      // Criar link para download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `planejamento_campanha_${formValues.anunciante}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: 'Planilha gerada com sucesso!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Erro ao gerar planilha:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao gerar planilha. Tente novamente.',
        severity: 'error',
      });
    } finally {
      setLoading(prev => ({ ...prev, submit: false }));
    }
  }, [formValues, configuracoesProgramaticaOpen, configuracoesProgramaticaEspecial, valorMinimoMensalEspeciais, validarProgramaticaEspecial]);

  // Função para lidar com a mudança de região


  // Se a página estiver carregando, exibir um spinner
  if (loading.page) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minWidth: 'max-content' }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Planejador de Campanhas
      </Typography>
      
      <Paper elevation={0} sx={{ p: 2, mb: 4, bgcolor: 'background.paper' }}>
        <Typography variant="body1">
          Preencha o formulário abaixo para gerar uma planilha de planejamento de campanha.
          Os campos são dependentes, então selecione as opções na ordem apresentada.
        </Typography>
      </Paper>
      
      <form onSubmit={handleSubmit}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'row',
          gap: 3,
          alignItems: 'flex-start',
          width: '100%',
          minWidth: 'max-content'
        }}>
          {/* Informações da Campanha - Lado Esquerdo */}
          <Card sx={{ width: '400px', flexShrink: 0 }}>
              <CardHeader
                title="Informações da Campanha"
                titleTypographyProps={{ variant: 'h6' }}
              />
              <Divider />
            <CardContent sx={{ width: '100%' }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 2,
                width: '100%'
              }}>
                  {/* Checkbox Comissão */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                  borderColor: 'divider',
                  width: '100%'
                  }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                                          checked={formValues.comissao}
                onChange={(e) => setFormValues(prev => ({ ...prev, comissao: e.target.checked }))}
                          sx={{
                            '& .MuiSvgIcon-root': {
                              fontSize: 22,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          Comissão
                        </Typography>
                      }
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: '1.1rem',
                        },
                      }}
                    />
                    <Tooltip title="Marque esta opção se a campanha incluir comissão. Isso incluirá 20% do bruto">
                      <HelpOutlineIcon sx={{ color: 'action.active', ml: 1, cursor: 'help' }} />
                    </Tooltip>
                  </Box>

                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                  borderColor: 'divider',
                  width: '100%'
                  }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formValues.bv}
                          onChange={(e) => setFormValues(prev => ({ ...prev, bv: e.target.checked }))}
                          sx={{
                            '& .MuiSvgIcon-root': {
                              fontSize: 22,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          BV
                        </Typography>
                      }
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          fontSize: '1.1rem',
                        },
                      }}
                    />
                    <Tooltip
                      title={
                        <span>
                          Marque esta opção se incide BV de até 10% lembrando que, Formatos Especiais: CTV, OTT, Web Push, In Game - Plataforma Etermax, não incidem BV. Caso a opção seja selecionada, não surtirá efeito em Programática Especial para tais formatos.<br />
                          Qualquer dúvida <a href="/criador" style={{ color: '#400047', textDecoration: 'underline' }}>clique aqui!</a>
                        </span>
                      }
                      componentsProps={{ tooltip: { sx: { maxWidth: 350 } } }}
                    >
                      <HelpOutlineIcon sx={{ color: 'action.active', ml: 1, cursor: 'pointer' }} />
                    </Tooltip>
                  </Box>



                  <TextField
                    label="Anunciante"
                    value={formValues.anunciante}
                    onChange={handleInputChange('anunciante')}
                    required
                  sx={{ 
                    width: '100%',
                    '& .MuiInputBase-root': {
                      minHeight: '56px',
                    }
                  }}
                  />

                  <TextField
                    label="Produto"
                    value={formValues.produto}
                    onChange={handleInputChange('produto')}
                    required
                  sx={{ 
                    width: '100%',
                    '& .MuiInputBase-root': {
                      minHeight: '56px',
                    }
                  }}
                />

                      <DatePicker
                        label="Data de Início"
                        value={formValues.dataInicio}
                        onChange={handleDateChange('dataInicio')}
                        slotProps={{
                          textField: {
                            required: true,
                      sx: { 
                        width: '100%',
                        '& .MuiInputBase-root': {
                          minHeight: '56px',
                        }
                      }
                          },
                        }}
                      />

                      <DatePicker
                        label="Data de Fim"
                        value={formValues.dataFim}
                        onChange={handleDateChange('dataFim')}
                        slotProps={{
                          textField: {
                            required: true,
                      sx: { 
                        width: '100%',
                        '& .MuiInputBase-root': {
                          minHeight: '56px',
                        }
                      }
                          },
                        }}
                      />

                  {/* Campo de valor removido visualmente mas mantido no código
                  <TextField
                    label="Valor"
                    value={formatCurrencyInput(
                      (
                        formValues.formato.reduce((total: number, formato: string) => {
                          const raw = formValues.valorPorFormato[formato] || '0';
                          return total + Number(raw) / 100;
                        }, 0)
                        + (habilitarEspeciais && valorMinimoMensalEspeciais
                          ? parseFloat(valorMinimoMensalEspeciais.replace(/\./g, '').replace(',', '.'))
                          : 0)
                      ).toFixed(2)
                    )}
                    disabled
                    type="text"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    }}
                  sx={{ 
                    width: '100%',
                    '& .MuiInputBase-root': {
                      minHeight: '56px',
                    }
                  }}
                  />
                  */}



                  {/* Campos de valor por formato */}
                  {formValues.formato.map((formato) => (
                    <TextField
                      key={formato}
                      label={`Valor ${formato}`}
                      value={formatCurrencyInput(formValues.valorPorFormato[formato] || '')}
                      onChange={handleValorChange(formato)}
                      required
                      type="text"
                      inputProps={{
                        inputMode: 'decimal'
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                      }}
                    sx={{ 
                      width: '100%',
                      '& .MuiInputBase-root': {
                        minHeight: '56px',
                      }
                      }}
                    />
                  ))}
              </Box>
              </CardContent>
            </Card>

          {/* Lado Direito - Programática Open e Programática Especial em coluna */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 3,
            flex: '1 1 auto',
            minWidth: 'max-content'
          }}>
            {/* Programática Open - Topo */}
            <Card sx={{ width: '100%', minWidth: 'max-content' }}>
              <CardHeader
                title="Programática Open"
                titleTypographyProps={{ variant: 'h6' }}
              />
              <Divider />
              <CardContent sx={{ width: '100%', minWidth: 'max-content' }}>
                {configuracoesProgramaticaOpen.length === 0 ? (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    py: 4,
                    color: 'text.secondary'
                  }}>
                    <Typography variant="body1">
                      Clique em "Adicionar Programática Open" para começar
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {configuracoesProgramaticaOpen.map((config, index) => (
                      <Box key={config.id} sx={{ 
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 2,
                        position: 'relative'
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          mb: 2,
                          pb: 1,
                          borderBottom: '1px solid',
                          borderColor: 'divider'
                        }}>
                          <Typography variant="h6" sx={{ color: 'primary.main' }}>
                            Configuração {index + 1}
                          </Typography>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => removerConfiguracaoProgramaticaOpen(config.id)}
                            sx={{ minWidth: 'auto', px: 2 }}
                          >
                            Remover
                          </Button>
                        </Box>
                        
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'row',
                          flexWrap: 'nowrap',
                          gap: 1.5,
                          alignItems: 'flex-start',
                          width: 'max-content'
                        }}>
                  <FormControl
                    required
                    sx={{
                              minWidth: 120,
                              flex: '0 0 140px',
                              '& .MuiSelect-select': {
                                minHeight: '56px !important',
                                display: 'flex',
                                alignItems: 'center',
                      }
                    }}
                  >
                            <InputLabel disabled={loading.objetivos}>Objetivo</InputLabel>
                    <Select
                              value={config.objetivo}
                      label="Objetivo"
                              onChange={(event) => {
                                const value = event.target.value;
                                atualizarConfiguracaoProgramaticaOpen(config.id, { objetivo: value });
                              }}
                              disabled={loading.objetivos}
                    >
                      {objetivos.map((objetivo) => (
                        <MenuItem key={objetivo} value={objetivo}>
                          {objetivo}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                          
                          <FormControl 
                            required 
                            disabled={!config.objetivo || loading.plataformas}
                            sx={{ 
                              minWidth: 120, 
                              flex: '0 0 140px',
                              '& .MuiSelect-select': {
                                minHeight: '56px !important',
                                display: 'flex',
                                alignItems: 'center',
                              }
                            }}
                          >
                    <InputLabel>Plataforma</InputLabel>
                    <Select
                              value={config.plataforma}
                      label="Plataforma"
                              onChange={(event) => {
                                const value = event.target.value;
                                atualizarConfiguracaoProgramaticaOpen(config.id, { plataforma: value });
                              }}
                    >
                      {plataformas.map((plataforma) => (
                        <MenuItem key={plataforma} value={plataforma}>
                          {plataforma}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                          
                          <FormControl 
                            required 
                            disabled={!config.plataforma}
                            sx={{ 
                              minWidth: 140, 
                              flex: '0 0 160px',
                              '& .MuiSelect-select': {
                                minHeight: '56px !important',
                                display: 'flex',
                                alignItems: 'center',
                              }
                            }}
                          >
                    <InputLabel>Device</InputLabel>
                    <Select
                              value={config.device}
                      label="Device"
                              onChange={(event) => {
                                const value = event.target.value;
                                atualizarConfiguracaoProgramaticaOpen(config.id, { device: value });
                              }}
                    >
                      <MenuItem value="Desktop">Desktop</MenuItem>
                      <MenuItem value="Mobile">Mobile</MenuItem>
                      <MenuItem value="Desktop e Mobile">Desktop e Mobile</MenuItem>
                    </Select>
                  </FormControl>
                          
                          {/* Formato - Seleção Única para Programática Open */}
                          <FormControl 
                            required 
                            disabled={!config.device || !config.plataforma}
                            sx={{ 
                              minWidth: 160, 
                              flex: '0 0 180px',
                              '& .MuiSelect-select': {
                                minHeight: '56px !important',
                                display: 'flex',
                                alignItems: 'center',
                              }
                            }}
                          >
                    <InputLabel>Formato</InputLabel>
                    <Select
                      multiple
                              value={config.formato}
                      label="Formato"
                              onChange={(event) => {
                                const value = event.target.value;
                                // Para programática open, permitir apenas uma seleção por vez
                                const formatosSelecionados = Array.isArray(value) ? value : [value];
                                // Se o usuário está desmarcando um formato, permitir
                                // Se está marcando um novo formato, substituir o anterior
                                const novoFormato = formatosSelecionados.length > 0 ? [formatosSelecionados[formatosSelecionados.length - 1]] : [];
                                atualizarConfiguracaoProgramaticaOpen(config.id, { 
                                  formato: novoFormato
                                });
                              }}
                      renderValue={(selected) => {
                        const formats = selected as string[];
                        if (formats.length === 0) return '';
                        // Para programática open, mostrar apenas o formato selecionado
                        return formats[0] || '';
                      }}
                    >
                              {getFormatosPorPlataforma(config.plataforma).map((formato) => (
                        <MenuItem key={formato} value={formato}>
                                  <Checkbox checked={config.formato.includes(formato)} />
                          <ListItemText primary={formato} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                          
                          <FormControl 
                            required 
                            disabled={!config.formato || config.formato.length === 0}
                            sx={{ 
                              minWidth: 140, 
                              flex: '0 0 160px',
                              '& .MuiSelect-select': {
                                minHeight: '56px !important',
                                display: 'flex',
                                alignItems: 'center',
                              }
                            }}
                          >
                    <InputLabel>Região</InputLabel>
                    <Select
                              value={config.regiao}
                      label="Região"
                              onChange={(event) => {
                                const value = event.target.value as Regiao;
                                atualizarConfiguracaoProgramaticaOpen(config.id, { regiao: value });
                              }}
                    >
                      <MenuItem value="Estados e Brasil">Estados e Brasil</MenuItem>
                      <MenuItem value="Grandes Metropoles">Grandes Metropoles</MenuItem>
                      <MenuItem value="Cidades pequenas">Cidades pequenas</MenuItem>
                    </Select>
                  </FormControl>
                          
                  {/* Dropdowns de estratégia para cada formato selecionado */}
                          {config.formato.map((formato) => (
                    <FormControl 
                      key={formato} 
                      required 
                              disabled={loading.estrategias || !estrategiasPorConfiguracao[config.id]?.[formato]}
                              sx={{ 
                                minWidth: 180, 
                                flex: '0 0 200px',
                                '& .MuiSelect-select': {
                                  minHeight: '56px !important',
                                  display: 'flex',
                                  alignItems: 'center',
                                }
                              }}
                    >
                      <InputLabel>Estratégia {formato}</InputLabel>
                      <Select
                        value={
                          formato === 'Youtube'
                                    ? (config.estrategiasPorFormato[formato]?.[0] || '')
                                    : (config.estrategiasPorFormato[formato] || [])
                        }
                        label={`Estratégia ${formato}`}
                        onChange={(event) => {
                          const value = event.target.value;
                                  const novasEstrategias = {
                                    ...config.estrategiasPorFormato,
                              [formato]: formato === 'Youtube'
                                ? (typeof value === 'string' && value !== '' ? [value] : [])
                                : (Array.isArray(value) ? value : [])
                                  };
                                  atualizarConfiguracaoProgramaticaOpen(config.id, { 
                                    estrategiasPorFormato: novasEstrategias
                                  });
                        }}
                        multiple={formato !== 'Youtube'}
                        renderValue={(selected) => {
                          if (formato === 'Youtube') {
                            if (!selected || selected === '') {
                              return <em>Selecione as estratégias</em>;
                            }
                            return selected;
                          } else {
                            if (!selected || (Array.isArray(selected) && selected.length === 0)) {
                              return <em>Selecione as estratégias</em>;
                            }
                            return (selected as string[]).join(', ');
                          }
                        }}
                      >
                        {estrategiasPorConfiguracao[config.id]?.[formato]?.map((estrategia: string) => (
                          <MenuItem key={estrategia} value={estrategia}>
                            {formato === 'Youtube' ? (
                              <ListItemText primary={estrategia} />
                            ) : (
                              <>
                                        <Checkbox checked={(config.estrategiasPorFormato[formato] || []).includes(estrategia)} />
                                <ListItemText primary={estrategia} />
                              </>
                            )}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ))}
                          
                  <FormControl 
                    required 
                            disabled={!config.estrategiasPorFormato || Object.values(config.estrategiasPorFormato).every(strategies => !strategies?.length)}
                            sx={{ 
                              minWidth: 120, 
                              flex: '0 0 140px',
                              '& .MuiSelect-select': {
                                minHeight: '56px !important',
                                display: 'flex',
                                alignItems: 'center',
                              }
                            }}
                  >
                    <InputLabel>Target</InputLabel>
                    <Select
                              value={config.target}
                      label="Target"
                              onChange={(event) => {
                                const value = event.target.value;
                                atualizarConfiguracaoProgramaticaOpen(config.id, { target: value });
                              }}
                    >
                      <MenuItem value="Homem">Homem</MenuItem>
                      <MenuItem value="Mulher">Mulher</MenuItem>
                      <MenuItem value="Ambos Sexos">Ambos Sexos</MenuItem>
                    </Select>
                  </FormControl>

                          {config.target && (
                  <FormControl 
                    required 
                    sx={{ 
                      minWidth: 140, 
                      flex: '0 0 160px',
                      '& .MuiSelect-select': {
                        minHeight: '56px !important',
                        display: 'flex',
                        alignItems: 'center',
                      }
                    }}
                  >
                      <InputLabel>Idade</InputLabel>
                      <Select
                                value={config.idade}
                        label="Idade"
                                onChange={(event) => {
                                  const value = event.target.value;
                                  atualizarConfiguracaoProgramaticaOpen(config.id, { 
                                    idade: Array.isArray(value) ? value : [value]
                                  });
                                }}
                        multiple
                        renderValue={(selected) => {
                          const ages = selected as string[];
                          if (ages.length === 0) return '';
                                  return ages.join(', ');
                        }}
                      >
                        <MenuItem value="18-24">
                                  <Checkbox checked={config.idade.includes('18-24')} />
                          <ListItemText primary="18-24" />
                        </MenuItem>
                        <MenuItem value="25-34">
                                  <Checkbox checked={config.idade.includes('25-34')} />
                          <ListItemText primary="25-34" />
                        </MenuItem>
                        <MenuItem value="35-44">
                                  <Checkbox checked={config.idade.includes('35-44')} />
                          <ListItemText primary="35-44" />
                        </MenuItem>
                        <MenuItem value="45-54">
                                  <Checkbox checked={config.idade.includes('45-54')} />
                          <ListItemText primary="45-54" />
                        </MenuItem>
                        <MenuItem value="55-65+">
                                  <Checkbox checked={config.idade.includes('55-65+')} />
                          <ListItemText primary="55-65+" />
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}

                  <TextField
                    required
                    label="Interesse"
                            value={config.interesse}
                            onChange={(e) => {
                              atualizarConfiguracaoProgramaticaOpen(config.id, { interesse: e.target.value });
                            }}
                    placeholder="Digite o interesse do público-alvo"
                    multiline
                    minRows={2}
                    maxRows={10}
                            disabled={!config.idade || config.idade.length === 0}
                    sx={{
                              minWidth: 160,
                              flex: '0 0 180px',
                              '& .MuiInputBase-root': {
                                minHeight: '80px',
                              },
                      '&.Mui-disabled': {
                        pointerEvents: 'none',
                        backgroundColor: 'transparent',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23)',
                        },
                      },
                    }}
                  />

                  {/* Campos de Praça para cada formato selecionado */}
                          {config.formato.map((formato) => (
                    <TextField
                      key={formato}
                      required
                      label={`Praça ${formato}`}
                              value={config.pracasPorFormato[formato] || ''}
                              onChange={(e) => {
                                const novasPracas = {
                                  ...config.pracasPorFormato,
                          [formato]: e.target.value
                                };
                                atualizarConfiguracaoProgramaticaOpen(config.id, { pracasPorFormato: novasPracas });
                              }}
                      placeholder={`Digite a praça do público-alvo para ${formato}`}
                      multiline
                      minRows={2}
                      maxRows={10}
                              disabled={!config.interesse}
                      sx={{
                                minWidth: 160,
                                flex: '0 0 180px',
                                '& .MuiInputBase-root': {
                                  minHeight: '80px',
                                },
                                '&.Mui-disabled': {
                                  pointerEvents: 'none',
                                  backgroundColor: 'transparent',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(0, 0, 0, 0.23)',
                                  },
                                },
                              }}
                            />
                          ))}

                          {/* Campos de Valor para cada formato selecionado */}
                          {config.formato.map((formato) => (
                            <TextField
                              key={formato}
                              required
                              label={`Valor ${formato}`}
                              value={formatCurrencyInput(config.valorPorFormato[formato] || '')}
                              onChange={(e) => {
                                const onlyNums = onlyNumbers(e.target.value);
                                const novosValores = {
                                  ...config.valorPorFormato,
                                  [formato]: onlyNums
                                };
                                atualizarConfiguracaoProgramaticaOpen(config.id, { valorPorFormato: novosValores });
                              }}
                              type="text"
                              inputProps={{
                                inputMode: 'decimal'
                              }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                              }}
                              disabled={!config.pracasPorFormato[formato]}
                              sx={{
                                minWidth: 140,
                                flex: '0 0 160px',
                                '& .MuiInputBase-root': {
                                  minHeight: '56px',
                                },
                        '&.Mui-disabled': {
                          pointerEvents: 'none',
                          backgroundColor: 'transparent',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                          },
                        },
                      }}
                    />
                  ))}

                  {/* Dropdowns de tipo de compra para cada formato selecionado */}
                          {config.formato.map((formato) => (
                    <FormControl 
                      key={formato} 
                      required 
                              disabled={!config.estrategiasPorFormato[formato]?.length || loading.compras}
                              sx={{ 
                                minWidth: 140, 
                                flex: '0 0 160px',
                                '& .MuiSelect-select': {
                                  minHeight: '56px !important',
                                  display: 'flex',
                                  alignItems: 'center',
                                }
                              }}
                    >
                      <InputLabel>{`Tipo de Compra ${formato}`}</InputLabel>
                      <Select
                                value={config.comprasPorFormato[formato]?.[0] || ''}
                        label={`Tipo de Compra ${formato}`}
                        onChange={(event) => {
                          const value = event.target.value as string;
                                  const novasCompras = {
                                    ...config.comprasPorFormato,
                              [formato]: [value]
                                  };
                                  atualizarConfiguracaoProgramaticaOpen(config.id, { comprasPorFormato: novasCompras });
                        }}
                        renderValue={(selected) => {
                          if (!selected) {
                            return <em>Selecione o tipo de compra</em>;
                          }
                          return selected;
                        }}
                      >
                        {tiposCompraPorFormato[formato]?.map((tipoCompra: string) => (
                          <MenuItem key={tipoCompra} value={tipoCompra}>
                            {tipoCompra}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ))}

                  {/* Campos de CTR/VTR para cada formato selecionado */}
                          {config.formato.map((formato) => (
                    !['Display', 'Alto Impacto'].includes(formato) && (
                      <TextField
                        key={formato}
                        required
                        label={formato.includes('Vídeo') || formato === 'Youtube' ? `VTR ${formato} (%)` : `CTR ${formato} (%)`}
                                value={config.ctrPorFormato?.[formato] || ''}
                        onChange={(e) => {
                                  const novosCtr = {
                                    ...config.ctrPorFormato,
                              [formato]: e.target.value
                                  };
                                  atualizarConfiguracaoProgramaticaOpen(config.id, { ctrPorFormato: novosCtr });
                        }}
                        placeholder={`Digite o ${formato.includes('Vídeo') || formato === 'Youtube' ? 'VTR' : 'CTR'} esperado para ${formato}`}
                        type="text"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                                disabled={!config.comprasPorFormato?.[formato]}
                        sx={{
                                  minWidth: 140,
                                  flex: '0 0 160px',
                                  '& .MuiInputBase-root': {
                                    minHeight: '56px',
                                  },
                          '&.Mui-disabled': {
                            pointerEvents: 'none',
                            backgroundColor: 'transparent',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(0, 0, 0, 0.23)',
                            },
                          },
                        }}
                      />
                    )
                  ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
                
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={adicionarConfiguracaoProgramaticaOpen}
                    sx={{
                      color: 'primary.main',
                      borderColor: 'primary.main',
                      '&:hover': {
                        borderColor: 'primary.dark',
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                      },
                    }}
                  >
                    Adicionar Programática Open
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Programática Especial - Abaixo da Programática Open */}
            <Card sx={{ width: '100%', minWidth: 'max-content' }}>
              <CardHeader
                title="Programática Especial"
                titleTypographyProps={{ variant: 'h6' }}
              />
              <Divider />
              <CardContent sx={{ width: '100%', minWidth: 'max-content' }}>
                {configuracoesProgramaticaEspecial.length === 0 ? (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    py: 4,
                    color: 'text.secondary'
                  }}>
                    <Typography variant="body1">
                      Clique em "Adicionar Programática Especial" para começar
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {configuracoesProgramaticaEspecial.map((config, index) => (
                      <Box key={config.id} sx={{ 
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 2,
                        position: 'relative'
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          mb: 2,
                          pb: 1,
                          borderBottom: '1px solid',
                          borderColor: 'divider'
                        }}>
                          <Typography variant="h6" sx={{ color: 'primary.main' }}>
                            Configuração Especial {index + 1}
                          </Typography>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => removerConfiguracaoProgramaticaEspecial(config.id)}
                            sx={{ minWidth: 'auto', px: 2 }}
                          >
                            Remover
                          </Button>
                        </Box>
                        
                <ConfiguracoesTecnicasEspeciais
                  bvChecked={formValues.bv}
                  enabled={true}
                  {...getConfiguracaoEspecialHandlers(config.id)}
                />
                      </Box>
                    ))}
                  </Box>
                )}
                
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={adicionarConfiguracaoProgramaticaEspecial}
                    sx={{
                      color: 'primary.main',
                      borderColor: 'primary.main',
                      '&:hover': {
                        borderColor: 'primary.dark',
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                      },
                    }}
                  >
                    Adicionar Programática Especial
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            startIcon={loading.submit ? <CircularProgress size={24} color="inherit" /> : <CloudDownloadIcon />}
            disabled={loading.submit}
            sx={{ py: 1.5, px: 4 }}
          >
            {loading.submit ? 'Gerando planilha...' : 'Gerar Planilha'}
          </Button>
        </Box>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Planejador; 